<template>
  <div class="space-y-2 text-left">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h4 class="font-semibold text-gray-800">开票信息</h4>
      <button
        type="button"
        class="text-sm text-blue-600 disabled:opacity-50"
        :disabled="sources.order.loading || sources.sale.loading"
        @click="refreshInvoices"
      >
        <i class="fas fa-sync-alt mr-1"></i>刷新
      </button>
    </div>
    <p v-if="!orderNo" class="text-sm text-red-600">订单号缺失，无法查询开票信息</p>
    <p
      v-else-if="
        sources.order.loaded &&
        sources.sale.loaded &&
        !sources.order.error &&
        !sources.sale.error &&
        !sources.order.records.length &&
        !sources.sale.records.length
      "
      class="py-6 text-center text-gray-500"
    >
      暂无开票信息
    </p>
    <section v-for="source in visibleSources" :key="source" class="space-y-2">
      <p v-if="sources[source].loading" role="status" class="text-sm text-gray-500">
        <i class="fas fa-spinner fa-spin mr-1"></i>正在查询{{ sourceLabels[source] }}…
      </p>
      <div
        v-if="sources[source].error"
        role="alert"
        class="flex flex-wrap items-center gap-3 rounded-lg bg-red-50 p-3 text-sm text-red-600"
      >
        <span>{{ sourceLabels[source] }}查询失败：{{ sources[source].error }}</span>
        <button
          type="button"
          class="text-blue-600 disabled:opacity-50"
          :disabled="sources[source].loading"
          @click="loadSource(source)"
        >
          重试
        </button>
      </div>
      <article
        v-for="invoice in sources[source].records"
        :key="'orderInvoiceId' in invoice ? invoice.orderInvoiceId : invoice.saleInvoiceId"
        class="min-w-0 space-y-2 rounded-lg border border-gray-200 p-3"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm font-semibold text-gray-800">{{ sourceLabels[source] }}</span>
            <span
              class="text-xs font-medium"
              :class="invoice.invoiceStatus === 10 ? 'text-green-600' : 'text-gray-600'"
            >
              {{ invoiceStatusText(invoice.invoiceStatus) }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-2 text-xs">
            <a
              v-if="safeInvoiceUrl(invoice.invoiceUrl)"
              :href="safeInvoiceUrl(invoice.invoiceUrl)"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center rounded border border-gray-300 px-3 py-1 text-gray-600 hover:bg-gray-50"
            >
              <i class="fas fa-eye mr-1"></i>预览发票
            </a>
            <a
              v-if="safeInvoiceUrl(invoice.invoiceUrlDownload)"
              :href="safeInvoiceUrl(invoice.invoiceUrlDownload)"
              download
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center rounded border border-blue-500 bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
            >
              <i class="fas fa-download mr-1"></i>下载发票
            </a>
            <span v-else class="text-gray-500">暂无下载地址</span>
          </div>
        </div>
        <dl class="grid grid-cols-1 gap-x-4 gap-y-1 text-sm leading-6 md:grid-cols-2">
          <div
            v-for="field in invoiceFields(invoice)"
            :key="field.label"
            class="flex min-w-0 items-start"
          >
            <dt class="w-20 flex-shrink-0 font-normal text-gray-500">{{ field.label }}：</dt>
            <dd class="m-0 min-w-0 flex-1 break-all text-gray-800">{{ field.value }}</dd>
          </div>
        </dl>
      </article>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, reactive, watch } from 'vue';
  import dayjs from 'dayjs';
  import { getOrderInvoiceList, getSaleInvoiceList } from '@/service/orderInvoice';
  import type { OrderInvoiceInfoModel, SaleInvoiceInfoModel } from '@/model/orderInvoice';
  import { dateTimeFormat } from '@/utils/utils';

  const props = defineProps<{
    orderNo: string;
    shopId: number;
    active: boolean;
  }>();

  const sourceNames = ['order', 'sale'] as const;
  const sourceLabels = { order: '订单发票', sale: '销售发票' };
  const createSourceState = () => {
    const records: (OrderInvoiceInfoModel | SaleInvoiceInfoModel)[] = [];
    return { records, loading: false, loaded: false, error: '', requestId: 0 };
  };
  const sources = reactive({ order: createSourceState(), sale: createSourceState() });
  const visibleSources = computed(() =>
    sourceNames.filter((source) => {
      const state = sources[source];
      return state.loading || state.error || state.records.length > 0;
    }),
  );

  const invoiceStatusText = (status?: number | null) => {
    if (status === 1) return '待开票';
    if (status === 10) return '已开票';
    return '未知状态';
  };

  const invoiceFields = (invoice: OrderInvoiceInfoModel | SaleInvoiceInfoModel) => {
    const fields = [
      { label: '发票号', value: invoice.invoiceCode || '—' },
      { label: '订单号', value: invoice.orderNo || '—' },
      { label: '发票抬头', value: invoice.title || '—' },
      { label: '税号', value: invoice.taxNo || '—' },
      {
        label: '开票金额',
        value: invoice.invoiceAmount == null ? '—' : `¥${invoice.invoiceAmount.toFixed(2)}`,
      },
      { label: '接收邮箱', value: invoice.emailAddress || '—' },
      { label: '手机号', value: invoice.phone || '—' },
      {
        label: '创建时间',
        value: invoice.createTime ? dateTimeFormat(dayjs(invoice.createTime)) : '—',
      },
    ];
    if ('orderInvoiceId' in invoice) {
      fields.push(
        {
          label: '发票类型',
          value:
            invoice.invoiceTypeName ||
            (invoice.invoiceType === 1 ? '个人' : invoice.invoiceType === 2 ? '单位' : '—'),
        },
        { label: '发票内容', value: invoice.content || '—' },
        { label: '开户银行', value: invoice.bankName || '—' },
        { label: '银行账户', value: invoice.bankAccount || '—' },
        { label: '单位地址', value: invoice.companyAddress || '—' },
        { label: '单位电话', value: invoice.companyPhone || '—' },
      );
    }
    return fields;
  };

  const safeInvoiceUrl = (value?: string | null): string => {
    if (!value?.trim()) return '';
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch {
      return '';
    }
  };

  const loadSource = async (source: (typeof sourceNames)[number]) => {
    const state = sources[source];
    if (state.loading || !props.orderNo) return;
    const requestId = ++state.requestId;
    const { orderNo, shopId } = props;
    const query = source === 'order' ? getOrderInvoiceList : getSaleInvoiceList;
    state.loading = true;
    state.error = '';
    state.records = [];
    try {
      const records: (OrderInvoiceInfoModel | SaleInvoiceInfoModel)[] = [];
      const pageSize = 100;
      let pageNum = 1;
      while (requestId === state.requestId) {
        const response = await query({ pageNum, pageSize, data: { orderNo, shopId } });
        if (requestId !== state.requestId) return;
        if (response.code !== 200 || !response.result) {
          throw new Error(response.msg || '请稍后重试');
        }
        const page = response.result;
        records.push(...(page.list || []));
        if (!page.list?.length || records.length >= page.total) break;
        pageNum += 1;
      }
      state.records = records;
    } catch (error: unknown) {
      if (requestId !== state.requestId) return;
      console.error(`${sourceLabels[source]}查询失败:`, error);
      state.error = error instanceof Error ? error.message : '请稍后重试';
    } finally {
      if (requestId === state.requestId) {
        state.loading = false;
        state.loaded = true;
      }
    }
  };

  const refreshInvoices = () => {
    void loadSource('order');
    void loadSource('sale');
  };

  const resetSources = () => {
    for (const source of sourceNames) {
      const state = sources[source];
      state.requestId += 1;
      state.records = [];
      state.loading = false;
      state.loaded = false;
      state.error = '';
    }
  };

  watch(
    () => [props.orderNo, props.shopId] as const,
    () => {
      resetSources();
      if (props.active) refreshInvoices();
    },
  );
  watch(
    () => props.active,
    (active) => {
      if (active && !sources.order.loaded && !sources.sale.loaded) refreshInvoices();
    },
    { immediate: true },
  );
  onBeforeUnmount(resetSources);
</script>
